import React, { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import classes from './UtilizationBanner.module.scss';
import PageBanner from '@/components/Banner/PageBanner';
import Button from '@/components/Button/Button';
import { modalContext } from '@/context/modal.context';
import useAccount from '@/hooks/useAccount';
import useLog from '@/hooks/useLog';
import useMixpanel from '@/hooks/useMixpanel';
import Access from '@/utils/api/odas/access';
import { accessEventHelpers, resourceHelpers } from '@/utils/helpers';
import t from '@/utils/Language/Referral-translations';

const showUtilizationBanner = (accessEvent) => {
  return !!accessEvent && !accessEventHelpers.isClosedLoopReferral(accessEvent) && !accessEvent?.is_common_app;
};

const UtilizationBanner = ({ opportunity }) => {
  const [accessEvent, setAccessEvent] = useState(null);
  const [processing, setProcessing] = useState(false);
  const { enqueueModal } = useContext(modalContext);
  const { account } = useAccount();
  const { track } = useMixpanel();
  const { log } = useLog();
  const router = useRouter();

  useEffect(() => {
    if (!account?.state?.isLoggedIn || !opportunity?.id) {
      setAccessEvent(null);
      return;
    }

    Access.getByResource({ resource: opportunity }).then((response) => {
      setAccessEvent(response?.data ? { ...response.data, resource: opportunity } : null);
    });
  }, [opportunity, account?.state?.isLoggedIn]);

  const changeAccessEventStatus = (status) => {
    return Access.createOrUpdate({ resource: opportunity, status, direct: accessEvent.direct_access }).then((response) => {
      setProcessing(false);
      setAccessEvent({ ...response.data, resource: opportunity });
      account.updateAccessEventCollection({ action: 'statusChange', accessEvent: response.data });
      return response.data;
    });
  };

  // Persists the status change, then fires the status-specific analytics: every status writes
  // one activity-log entry, and some also fire extra Mixpanel events or open a follow-up modal
  // (review prompt on "utilized", related-resources suggestions on "rejected").
  const logAction = (status) => {
    setProcessing(true);
    return changeAccessEventStatus(status).then(() => {
      const eventHash = resourceHelpers.mixpanelEventHash(opportunity);
      // `via` defaults to the tracking source (query `s`, else 'opportunity button'), matching
      // Angular's track_access_button logs. The paired 'identified need' log on 'planned' is the
      // exception: Angular hard-codes its `via` to 'resource access planned', so it's passed in.
      const logEntry = (event, via = router.query.s || 'opportunity button') =>
        log({
          event,
          via,
          message: opportunity.slug,
          object_type: opportunity.resource_type,
          object_id: opportunity.id
        });

      switch (status) {
        case 'planned':
          track('Resource access planned', eventHash);
          track('Identified need', { ...eventHash, sourceEvent: 'resource access planned' });
          logEntry('resource access planned');
          logEntry('identified need', 'resource access planned');
          break;
        case 'accessed':
          track('Resource accessed', eventHash);
          track('Took action to access', { ...eventHash, sourceEvent: 'resource accessed track button' });
          logEntry('resource accessed');
          logEntry('took action to access');
          break;
        case 'utilized':
          track('Utilized resource', eventHash);
          logEntry('utilized resource');
          enqueueModal({ name: 'NewReviewModal', accessEvent, resource: opportunity, user: account.data });
          break;
        case 'unsuccessful':
          track('Resource access unsuccessful', eventHash);
          logEntry('resource access unsuccessful');
          break;
        case 'rejected':
          track('Resource access rejected', eventHash);
          logEntry('resource access rejected');
          enqueueModal({
            name: 'RelatedResourcesModal',
            resetAccessEventStatus: () => changeAccessEventStatus('planned'),
            resource: opportunity,
            user: account.data
          });
          break;
      }
    });
  };

  const openChangeStatusModal = () => {
    enqueueModal({
      name: 'ChangeAccessEventStatusModal',
      onUtilized: () => logAction('utilized'),
      onRejected: () => logAction('rejected'),
      onReset: () => logAction('planned')
    });
  };

  if (!showUtilizationBanner(accessEvent)) {
    return null;
  }

  const changeStatusButton = (
    <Button
      type="link"
      disabled={processing}
      text={'(' + t('Change my status') + ')'}
      additionalClasses={classes.changeStatusButton}
      onButtonClick={openChangeStatusModal}
    />
  );

  if (accessEventHelpers.isCompleted(accessEvent)) {
    return (
      <PageBanner type="info" icon={['fas', 'circle-check']} iconColor="blue">
        {t('I used it!')} {changeStatusButton}
      </PageBanner>
    );
  }

  if (accessEventHelpers.isArchived(accessEvent)) {
    return (
      <PageBanner type="info" icon={['fas', 'circle-xmark']} iconColor="red">
        {t(accessEvent.status === 'unsuccessful' ? "I'm not eligible" : 'I decided not to use it')} {changeStatusButton}
      </PageBanner>
    );
  }

  return (
    <PageBanner type="info" icon={['fas', 'circle-question']} iconColor="blue">
      <div className={classes.askRow}>
        <span>{t('Did you use this service?')}</span>
        <Button
          dataCy="utilization-banner-yes"
          type="default"
          disabled={processing}
          text={t('Yes')}
          icon={{ style: 'fas', font: 'check', position: 'left' }}
          onButtonClick={() => logAction('utilized')}
        />
        <Button
          dataCy="utilization-banner-no"
          type="default"
          disabled={processing}
          text={t('No')}
          icon={{ style: 'fas', font: 'times', position: 'left' }}
          onButtonClick={() => logAction('rejected')}
        />
      </div>
    </PageBanner>
  );
};

export default UtilizationBanner;
